import { defineNuxtPlugin, useNuxtApp, useRuntimeConfig } from '#app'
import { posthog, type PostHog, type PostHogConfig } from 'posthog-js'
import { nextTick, ref, watch } from 'vue'
import type { AxeptioChoices, AxeptioSDK } from './axeptio.client'
import { useCookie, useRouter } from '#imports'

declare module '#app' {
  interface NuxtApp {
    $posthog: () => PostHog | null
  }
}

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig()
  const router = useRouter()
  const { $stores } = useNuxtApp()
  const posthogClient = ref<typeof posthog | null>(null)
  const cookieName = `ph_${String(runtimeConfig.public.posthogPublicKey)}`

  function initPostHog() {
    posthogClient.value = posthog.init(runtimeConfig.public.posthogPublicKey, {
      api_host: runtimeConfig.public.posthogHost,
      person_profiles: 'always', // or 'always' to create profiles for anonymous users as well // identified_only
      capture_pageview: false, // we add manual pageview capturing below
      capture_pageleave: false,
      enable_heatmaps: false,
      autocapture: false, // enable automatic event capturing
      persistence: 'memory', // leave to memory on initialization, will be changed after based on user consent
      loaded: (posthogInstance) => {
        if (import.meta.env.MODE === 'development') {
          posthogInstance.debug()
        }
      },
    })
  }

  function identifyUser() {
    const currentUser = $stores.auth.user
    posthogClient.value?.identify($stores.auth.user?.id.toString(), {
      userId: currentUser?.id,
      contextRole: $stores.auth.contextRole,
    })
  }

  function resetUser() {
    posthogClient.value?.reset()
  }

  function activatePosthog() {
    changePersistance('localStorage+cookie')
  }

  function deactivatePosthog() {
    changePersistance('memory')
    const cookiePosthog = useCookie(cookieName)
    cookiePosthog.value = null
    localStorage?.removeItem(cookieName)
  }

  function changePersistance(state: PostHogConfig['persistence']) {
    if (!posthogClient.value) {
      console.warn('PostHog client is not initialized.')
      return
    }

    const distinctId = posthogClient.value.get_distinct_id()
    posthogClient.value.set_config({ persistence: state })
    posthogClient.value.identify(distinctId)
  }

  if (import.meta.client) {
    initPostHog()
    if ($stores.auth.isLogged) {
      identifyUser()
    }

    window._axcb?.push((axeptio: AxeptioSDK) => {
      axeptio.on('cookies:complete', (choices: AxeptioChoices) => {
        choices.posthog_custom ? activatePosthog() : deactivatePosthog()
      })
    })

    // If user already has completed choices, request consent for posthog_custom if it is missing
    // window._axcb?.push(function (axeptio: AxeptioSDK) {
    //   if (
    //     axeptio.userPreferencesManager.choices.$$completed === true &&
    //     axeptio.userPreferencesManager.choices.posthog_custom === undefined
    //   ) {
    //     axeptio.requestConsent('posthog_custom')
    //   }
    // })
  }

  // Define wildcard routes to exclude from pageview capture
  const excludedRoutes = [
    /^\/missions-benevolat\/.*$/,
    /^\/admin(\/.*)?$/, // matches /admin and /admin/**
    /^\/dashboard$/, // matches exactly /dashboard
  ]

  router.afterEach((to) => {
    // Check if the current route matches any excluded pattern
    const isExcluded = excludedRoutes.some((pattern) => pattern.test(to.fullPath))
    if (isExcluded) {
      console.warn(`Pageview capture skipped for route: ${to.fullPath}`)
      return
    }

    nextTick(() => {
      posthogClient.value?.capture('$pageview', {
        current_url: to.fullPath,
      })
    }).catch((error) => {
      console.error('Error capturing pageview:', error)
    })
  })

  watch(
    () => $stores.auth.isLogged,
    (isLogged) => {
      if (!posthogClient.value) {
        return
      }

      if (isLogged) {
        identifyUser()
      } else {
        resetUser()
      }
    },
  )

  return {
    provide: {
      posthog: () => posthogClient.value,
    },
  }
})
